/*
  EasyUSA cart-drawer motion — the second half of the add-to-cart morph
  (assets/eu-atc-morph.js). Once the button has confirmed:

    • a new line slides into the drawer, opening its own slot
    • a line whose quantity went up gets a brief highlight
    • the subtotal counts up from the old total to the new one
    • the header cart badge pops. The drawer covers the header's cart icon
      while it's open, so the pop waits until the drawer closes — the item
      "lands" in the cart as you go back to shopping.

  The minicart calls snapshot() before it swaps in fresh HTML and play()
  after. The badge watcher needs no wiring: it observes the count elements
  of both headers (classic + minimal), so neither header file changes.
  Reduced motion: every value still updates, nothing moves.
*/
(function () {
  'use strict';
  if (window.EuCartMotion) return;

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(reduceQuery && reduceQuery.matches); }

  // Closed-form spring step response, same as the ATC morph, sampled into a
  // CSS linear() easing so the Web Animations API can run it.
  function stepResponse(t, omega, zeta) {
    if (t <= 0) return 0;
    if (zeta >= 1) return 1 - Math.exp(-omega * t) * (1 + omega * t);
    var wd = omega * Math.sqrt(1 - zeta * zeta);
    return 1 - Math.exp(-zeta * omega * t) *
      (Math.cos(wd * t) + (zeta * omega / wd) * Math.sin(wd * t));
  }
  function springEasing(durationMs, omega, zeta) {
    var points = [];
    for (var i = 0; i <= 32; i++) {
      points.push(stepResponse((durationMs / 1000) * (i / 32), omega, zeta).toFixed(4));
    }
    points[points.length - 1] = '1';
    return 'linear(' + points.join(', ') + ')';
  }
  var SETTLE = springEasing(460, 18, 0.9);    // slot opening, tiny overshoot at most
  var FALLBACK_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

  function animate(el, frames, opts) {
    if (!el.animate) return null;
    try {
      return el.animate(frames, opts);
    } catch (e) {
      // Older browsers without linear() easing.
      opts.easing = FALLBACK_EASE;
      return el.animate(frames, opts);
    }
  }

  // ── Money count-up ─────────────────────────────────────────────
  var usd = window.Intl ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }) : null;
  function fmt(cents) {
    return usd ? usd.format(cents / 100) : '$' + (cents / 100).toFixed(2);
  }

  // Rolls el's text from fromCents to toCents, then settles on finalText
  // (the server-rendered string) so the end state is exactly what Liquid
  // would have printed.
  function rollMoney(el, fromCents, toCents, finalText, delay) {
    if (!el) return;
    finalText = finalText || fmt(toCents);
    if (el._euRoll) cancelAnimationFrame(el._euRoll);
    if (reduced() || fromCents === toCents || isNaN(fromCents) || isNaN(toCents)) {
      el.textContent = finalText;
      return;
    }
    var duration = 650;
    var start = performance.now() + (delay || 0);
    el.textContent = fmt(fromCents);
    function tick(now) {
      var t = (now - start) / duration;
      if (t >= 1) { el.textContent = finalText; el._euRoll = 0; return; }
      if (t > 0) {
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = fmt(Math.round(fromCents + (toCents - fromCents) * eased));
      }
      el._euRoll = requestAnimationFrame(tick);
    }
    el._euRoll = requestAnimationFrame(tick);
  }

  // ── Drawer lines ───────────────────────────────────────────────
  function lineQty(row) {
    var q = row.querySelector('.mc-qty-val');
    return q ? parseInt(q.textContent, 10) || 0 : 0;
  }

  function snapshot(panel) {
    var lines = {};
    panel.querySelectorAll('.mc-item[data-line-key]').forEach(function (row) {
      lines[row.dataset.lineKey] = lineQty(row);
    });
    var total = panel.querySelector('.mc-total__value[data-cents]');
    return {
      lines: lines,
      cents: total ? parseInt(total.dataset.cents, 10) : NaN,
      hadLines: Object.keys(lines).length > 0
    };
  }

  function arrive(row, delay) {
    var cs = getComputedStyle(row);
    // Computed height matches the row's own box-sizing, so padding/border
    // aren't counted twice (offsetHeight would make it snap at the end).
    var h = cs.height;
    row.style.overflow = 'hidden';
    var anim = animate(row, [
      { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginBottom: '0px',
        borderTopWidth: '0px', borderBottomWidth: '0px', opacity: 0,
        transform: 'translateX(18px)', filter: 'blur(4px)' },
      { opacity: 0, offset: 0.35 },
      { height: h, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom,
        marginBottom: cs.marginBottom, borderTopWidth: cs.borderTopWidth,
        borderBottomWidth: cs.borderBottomWidth, opacity: 1,
        transform: 'translateX(0)', filter: 'blur(0)' }
    ], { duration: 460, delay: delay, easing: SETTLE, fill: 'backwards' });
    if (anim) anim.onfinish = anim.oncancel = function () { row.style.overflow = ''; };
    else row.style.overflow = '';
  }

  function bump(row, delay) {
    animate(row, [
      { boxShadow: '0 0 0 2px rgba(46, 126, 196, 0.55)' },
      { boxShadow: '0 0 0 2px rgba(46, 126, 196, 0)' }
    ], { duration: 900, delay: delay, easing: 'ease-out' });
    var qty = row.querySelector('.mc-qty-val');
    if (qty) {
      animate(qty, [
        { transform: 'scale(1)' }, { transform: 'scale(1.3)', offset: 0.35 }, { transform: 'scale(1)' }
      ], { duration: 420, delay: delay, easing: 'ease-out' });
    }
  }

  // Keep a newly arrived line visible inside the drawer's own scroll area
  // (never scrollIntoView — that can scroll the page behind the drawer).
  function reveal(row) {
    var body = row.closest('.mc-body');
    if (!body) return;
    var top = row.offsetTop - body.offsetTop;
    if (top < body.scrollTop) body.scrollTop = top - 10;
    else if (top + row.offsetHeight > body.scrollTop + body.clientHeight) {
      body.scrollTop = top + row.offsetHeight - body.clientHeight + 10;
    }
  }

  // opts.delay: wait for the drawer's slide-in before moving anything.
  function play(panel, before, opts) {
    if (!before) return;
    var delay = (opts && opts.delay) || 0;
    var motion = !reduced();

    // Nothing to compare against on an empty → first-item render; the
    // drawer's own slide-in is the arrival.
    if (motion && before.hadLines) {
      var arrived = null;
      panel.querySelectorAll('.mc-item[data-line-key]').forEach(function (row) {
        var key = row.dataset.lineKey;
        if (!(key in before.lines)) {
          if (!arrived) arrived = row;
          arrive(row, delay);
        } else if (lineQty(row) > before.lines[key]) {
          if (!arrived) arrived = row;
          bump(row, delay);
        }
      });
      if (arrived) reveal(arrived);
    }

    var total = panel.querySelector('.mc-total__value[data-cents]');
    if (total) {
      rollMoney(total, before.cents, parseInt(total.dataset.cents, 10),
        total.textContent, motion ? delay + 120 : 0);
    }
  }

  // ── Header badge pop ───────────────────────────────────────────
  var BADGE_IDS = ['eu-cart-count', 'eu-mobile-cart-count', 'eu-min-cart-count'];
  var pendingPop = false;

  function drawerOpen() {
    var panel = document.getElementById('eu-minicart');
    return !!(panel && panel.classList.contains('eu-minicart--open'));
  }

  function popBadges() {
    pendingPop = false;
    if (reduced()) return;
    BADGE_IDS.forEach(function (id) {
      var el = document.getElementById(id);
      if (!el || el.offsetParent === null) return;
      animate(el, [
        { transform: 'scale(1)' },
        { transform: 'scale(1.35)', offset: 0.3 },
        { transform: 'scale(1)' }
      ], { duration: 520, easing: SETTLE });
    });
  }

  function watchBadges() {
    BADGE_IDS.forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      var last = parseInt(el.textContent, 10) || 0;
      new MutationObserver(function () {
        var now = parseInt(el.textContent, 10) || 0;
        if (now > last) {
          if (drawerOpen()) pendingPop = true;
          else popBadges();
        }
        last = now;
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });

    var panel = document.getElementById('eu-minicart');
    if (!panel) return;
    var wasOpen = drawerOpen();
    new MutationObserver(function () {
      var open = drawerOpen();
      // Wait for the drawer to slide off the icon (240ms) before popping.
      if (wasOpen && !open && pendingPop) setTimeout(popBadges, 220);
      wasOpen = open;
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });
  }

  window.EuCartMotion = { snapshot: snapshot, play: play, rollMoney: rollMoney };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBadges);
  } else {
    watchBadges();
  }
}());
